import { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import {
  Search,
  Filter,
  Sparkles,
  X,
  Send,
  AlertTriangle,
  Plus,
  Megaphone,
  Layers,
  Link2,
  KeyRound,
  Tag,
  Wand2,
  Smartphone,
  Monitor,
  RefreshCw,
  CheckCircle2,
  ChevronDown,
  ArrowUpRight,
  ArrowDownRight,
  MessageCircle,
  Loader2,
  LayoutDashboard,
  BarChart3,
  Search as SearchIcon,
  Lightbulb,
} from "lucide-react";
import { toast } from "sonner";
import AriaTab from "./googleads/AriaTab";
import PerformanceTab from "./googleads/PerformanceTab";
import SearchTermsTab from "./googleads/SearchTermsTab";
import InsightsTab from "./googleads/InsightsTab";
import type { AriaTrigger } from "./googleads/shared";

const BURNT = "#EA580C";
const BURNT_GLOW = "#FB923C";

/* ======================================================================
   THEME TOKENS
====================================================================== */
const BG = "#070B14";
const BLUE = "#4285F4";
const ORANGE = "#FBBC04";
const GREEN = "#34A853";
const RED = "#EA4335";

const glass = {
  background:
    "linear-gradient(160deg, rgba(15,22,38,0.78), rgba(8,12,22,0.85))",
  border: "1px solid rgba(255,255,255,0.08)",
  backdropFilter: "blur(14px)",
  boxShadow:
    "0 1px 0 rgba(255,255,255,0.04) inset, 0 24px 60px -30px rgba(0,0,0,0.6)",
};

/* ======================================================================
   MOCK DATA
====================================================================== */
type Campaign = {
  id: string;
  name: string;
  enabled: boolean;
  budget: number;
  spend: number;
  clicks: number;
  conversions: number;
  impressions: number;
  revenue: number;
};

const INITIAL_CAMPAIGNS: Campaign[] = [
  { id: "c1", name: "Brand Awareness Q2", enabled: true, budget: 120, spend: 1842, clicks: 3842, conversions: 78, impressions: 92450, revenue: 9420 },
  { id: "c2", name: "Lead Gen Search", enabled: true, budget: 220, spend: 4210, clicks: 5104, conversions: 184, impressions: 164210, revenue: 28800 },
  { id: "c3", name: "Retargeting Display", enabled: true, budget: 60, spend: 844, clicks: 1622, conversions: 33, impressions: 248120, revenue: 4120 },
  { id: "c4", name: "Performance Max — Spring", enabled: false, budget: 180, spend: 1320, clicks: 1248, conversions: 22, impressions: 38420, revenue: 3120 },
  { id: "c5", name: "Local Service Ads", enabled: true, budget: 90, spend: 1806, clicks: 982, conversions: 61, impressions: 22440, revenue: 11200 },
];

const sparkData = (seed: number) =>
  Array.from({ length: 18 }, (_, i) => ({
    v: 40 + Math.sin(i / 2 + seed) * 18 + ((i * (seed % 4)) % 12) + ((seed * 5) % 9),
  }));

/* ======================================================================
   PRIMITIVES
====================================================================== */
const Glass = ({
  children,
  className = "",
  style = {},
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) => (
  <div
    className={`rounded-2xl ${className}`}
    style={{ ...glass, ...style }}
  >
    {children}
  </div>
);

/* ======================================================================
   METRIC CARD
====================================================================== */
const MetricCard = ({
  label,
  value,
  delta,
  up,
  accent,
  seed,
}: {
  label: string;
  value: string;
  delta: string;
  up: boolean;
  accent: string;
  seed: number;
}) => (
  <Glass className="group relative overflow-hidden p-5 transition-all duration-300 hover:-translate-y-0.5">
    <div
      aria-hidden
      className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-30 blur-3xl transition-opacity group-hover:opacity-60"
      style={{ background: accent }}
    />
    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50">
      {label}
    </div>
    <div className="mt-2 flex items-end justify-between">
      <div className="text-[28px] font-bold leading-none tracking-tight text-white">
        {value}
      </div>
      <div
        className="flex items-center gap-1 text-[11px] font-bold"
        style={{ color: up ? GREEN : RED }}
      >
        {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
        {delta}
      </div>
    </div>
    <div className="mt-3 h-10">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={sparkData(seed)}>
          <defs>
            <linearGradient id={`spark-${seed}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity={0.55} />
              <stop offset="100%" stopColor={accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={accent}
            strokeWidth={2}
            fill={`url(#spark-${seed})`}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </Glass>
);

/* ======================================================================
   CAMPAIGNS TABLE
====================================================================== */
type SortKey = keyof Campaign | "ctr" | "roas";
type StatusFilter = "all" | "active" | "paused";

const CampaignsTable = ({
  campaigns,
  onToggle,
}: {
  campaigns: Campaign[];
  onToggle: (id: string) => void;
}) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("spend");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const rows = useMemo(() => {
    const enriched = campaigns.map((c) => ({
      ...c,
      ctr: c.impressions ? (c.clicks / c.impressions) * 100 : 0,
      roas: c.spend ? c.revenue / c.spend : 0,
    }));
    const filtered = enriched.filter((c) => {
      if (filter === "active" && !c.enabled) return false;
      if (filter === "paused" && c.enabled) return false;
      if (query && !c.name.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
    filtered.sort((a, b) => {
      const av = (a as any)[sortKey];
      const bv = (b as any)[sortKey];
      if (typeof av === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return filtered;
  }, [campaigns, query, filter, sortKey, sortDir]);

  const sortBy = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const Th = ({ k, children }: { k: SortKey; children: React.ReactNode }) => (
    <th
      onClick={() => sortBy(k)}
      className="cursor-pointer select-none px-4 py-3 font-semibold uppercase tracking-[0.1em] text-[10.5px] text-white/45 hover:text-white"
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortKey === k && (
          <ChevronDown
            size={12}
            className={`transition-transform ${sortDir === "asc" ? "rotate-180" : ""}`}
          />
        )}
      </span>
    </th>
  );

  return (
    <Glass className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 px-5 py-4">
        <div>
          <h2 className="text-[16px] font-bold tracking-tight text-white">Campaigns</h2>
          <p className="text-[12px] text-white/50">
            {rows.length} of {campaigns.length} campaigns
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search campaigns..."
              className="h-9 w-56 rounded-lg border border-white/10 bg-white/[0.03] pl-9 pr-3 text-[12.5px] text-white placeholder:text-white/30 focus:border-white/20 focus:outline-none"
            />
          </div>
          <div className="relative">
            <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as StatusFilter)}
              className="h-9 appearance-none rounded-lg border border-white/10 bg-white/[0.03] pl-9 pr-8 text-[12.5px] text-white focus:border-white/20 focus:outline-none"
            >
              <option value="all">All Campaigns</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
            <ChevronDown size={12} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/40" />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-[12.5px]">
          <thead className="bg-white/[0.02]">
            <tr>
              <Th k="name">Campaign</Th>
              <Th k="enabled">Status</Th>
              <Th k="budget">Daily Budget</Th>
              <Th k="spend">Spend</Th>
              <Th k="clicks">Clicks</Th>
              <Th k="conversions">Conv.</Th>
              <Th k="ctr">CTR</Th>
              <Th k="roas">ROAS</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr
                key={c.id}
                className="border-t border-white/5 transition-colors hover:bg-white/[0.025]"
              >
                <td className="px-4 py-3 font-semibold text-white">{c.name}</td>
                <td className="px-4 py-3">
                  <button
                    role="switch"
                    aria-checked={c.enabled}
                    onClick={() => onToggle(c.id)}
                    className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                    style={{ background: c.enabled ? "rgba(52,168,83,0.45)" : "rgba(255,255,255,0.1)" }}
                  >
                    <span
                      className="h-4 w-4 rounded-full transition-transform"
                      style={{
                        background: c.enabled ? GREEN : "rgba(255,255,255,0.5)",
                        transform: c.enabled ? "translateX(18px)" : "translateX(2px)",
                        boxShadow: c.enabled ? `0 0 10px ${GREEN}` : "none",
                      }}
                    />
                  </button>
                  <span
                    className="ml-2 text-[11px] font-semibold"
                    style={{ color: c.enabled ? GREEN : "rgba(255,255,255,0.4)" }}
                  >
                    {c.enabled ? "Enabled" : "Paused"}
                  </span>
                </td>
                <td className="px-4 py-3 text-white/80">${c.budget}</td>
                <td className="px-4 py-3 text-white/80">${c.spend.toLocaleString()}</td>
                <td className="px-4 py-3 text-white/80">{c.clicks.toLocaleString()}</td>
                <td className="px-4 py-3 font-semibold" style={{ color: GREEN }}>
                  {c.conversions}
                </td>
                <td className="px-4 py-3 text-white/80">{c.ctr.toFixed(2)}%</td>
                <td className="px-4 py-3 font-semibold" style={{ color: c.roas >= 3 ? GREEN : ORANGE }}>
                  {c.roas.toFixed(2)}x
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-white/40">
                  No campaigns match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Glass>
  );
};

/* ======================================================================
   AI ASSISTANT
====================================================================== */
type Mode = "standard" | "advanced";
type ChatMsg = {
  id: string;
  role: "user" | "assistant";
  content: string;
};

type AdPreview = {
  finalUrl: string;
  pathA: string;
  pathB: string;
  headlines: string[];
  descriptions: string[];
  sitelinks: { title: string; desc: string }[];
  callouts: string[];
};

const QUICK_ACTIONS: {
  key: string;
  title: string;
  desc: string;
  icon: React.ComponentType<any>;
  prompt: string;
}[] = [
  { key: "ad", title: "New Ad", desc: "Generate a responsive search ad", icon: Megaphone, prompt: "Create a new responsive search ad" },
  { key: "adgroup", title: "New Ad Group", desc: "Spin up an ad group with keywords", icon: Layers, prompt: "Create a new ad group" },
  { key: "sitelink", title: "New Sitelink", desc: "Add sitelink extensions", icon: Link2, prompt: "Create new sitelink extensions" },
  { key: "keywords", title: "New Keywords", desc: "Expand high-intent keywords", icon: KeyRound, prompt: "Suggest new keywords" },
  { key: "callout", title: "New Callout", desc: "Punchy callout extensions", icon: Tag, prompt: "Create callout extensions" },
  { key: "optimize", title: "Optimize Campaign", desc: "Audit and recommend changes", icon: Wand2, prompt: "Optimize my best campaign" },
];

const SAMPLE_AD: AdPreview = {
  finalUrl: "https://orbitrank.dev/google-ads",
  pathA: "AI",
  pathB: "Google-Ads",
  headlines: [
    "AI Google Ads Manager",
    "Launch Smarter Campaigns Today",
    "Cut CPC by 32% with AI",
    "Trusted by 4,200+ Brands",
    "Free 7-Day Trial — No Card",
    "Real-Time Ad Optimization",
    "Build Ads in Seconds",
    "Boost ROAS, Not Spend",
  ],
  descriptions: [
    "OrbitRank's AI builds, tests and optimizes Google Ads in real time. Higher conversions, lower CPC.",
    "Stop guessing. Let AI craft headlines, descriptions and keywords that convert. Try free today.",
    "Connect Google Ads in 30s. Watch your ROAS climb. Cancel anytime. Built for serious marketers.",
  ],
  sitelinks: [
    { title: "Live Demo", desc: "See OrbitRank in action" },
    { title: "Pricing", desc: "Plans from $29/mo" },
    { title: "Case Studies", desc: "How we 4x'd ROAS" },
    { title: "Free Audit", desc: "Get an AI account audit" },
  ],
  callouts: ["Free 7-day trial", "No credit card", "Cancel anytime", "AI-powered", "24/7 support"],
};

function generateStandardReply(input: string, campaigns: Campaign[]): string {
  const top = [...campaigns].sort((a, b) => b.revenue - a.revenue)[0];
  const totalSpend = campaigns.reduce((s, c) => s + c.spend, 0);
  const totalConv = campaigns.reduce((s, c) => s + c.conversions, 0);
  const avgCpc = totalSpend / Math.max(1, campaigns.reduce((s, c) => s + c.clicks, 0));
  const q = input.toLowerCase();

  if (/cpc|cost per click/.test(q)) {
    return `Your average CPC across active campaigns is **$${avgCpc.toFixed(2)}**. The lowest CPC is on *Retargeting Display* — typical for warm audiences. *Lead Gen Search* runs a higher CPC (~$0.82) but converts at 3.6%, so it remains your most profitable line.`;
  }
  if (/drop|down|decline/.test(q)) {
    return `Clicks dipped on *Performance Max — Spring* because it was paused 4 days ago. Across the rest of the account clicks are up **+18.2%** week-over-week. Want me to summarize what changed?`;
  }
  if (/top|best/.test(q)) {
    return `Your top performer right now is **${top.name}** — $${top.revenue.toLocaleString()} in revenue at a ${(top.revenue / top.spend).toFixed(2)}x ROAS. Strong creative + a tight keyword set is doing most of the work.`;
  }
  if (/how|doing|performance|overview/.test(q)) {
    return `Last 30 days: **${totalConv} conversions** on **$${totalSpend.toLocaleString()}** spend. ROAS is trending up. Two things to watch: Performance Max is paused, and Retargeting Display CTR is softening. Want a deeper dive on either?`;
  }
  return `Here's what stands out: ${top.name} is leading with $${top.revenue.toLocaleString()} revenue. Account-wide spend is $${totalSpend.toLocaleString()} with ${totalConv} conversions. Ask me about CPC trends, top ads, or any campaign by name.`;
}

const AdvancedWarningModal = ({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
    <Glass className="mx-4 w-full max-w-md p-6">
      <div className="mb-4 flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ background: `${ORANGE}22`, border: `1px solid ${ORANGE}55` }}
        >
          <AlertTriangle size={18} style={{ color: ORANGE }} />
        </div>
        <h3 className="text-[17px] font-bold text-white">Switch to Advanced Mode?</h3>
      </div>
      <p className="text-[13px] leading-relaxed text-white/65">
        Advanced mode allows direct modifications to your live Google Ads campaigns.
        Recommended for experienced Google Ads users only. All changes require your
        explicit approval before going live.
      </p>
      <div className="mt-6 flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-2 text-[12.5px] font-semibold text-white/80 transition-colors hover:bg-white/[0.06]"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="rounded-lg px-4 py-2 text-[12.5px] font-bold text-black transition-transform hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: ORANGE, boxShadow: `0 8px 24px ${ORANGE}55` }}
        >
          Enable Advanced Mode
        </button>
      </div>
    </Glass>
  </div>
);

const AdPreviewCard = ({
  ad,
  device,
}: {
  ad: AdPreview;
  device: "desktop" | "mobile";
}) => {
  const [h1, h2, h3] = ad.headlines;
  const [d1, d2] = ad.descriptions;
  return (
    <div
      className={`mx-auto rounded-xl bg-white p-5 text-[#202124] shadow-2xl transition-all ${
        device === "mobile" ? "max-w-[360px]" : "max-w-[560px]"
      }`}
      style={{ fontFamily: "arial, sans-serif" }}
    >
      <div className="mb-1 flex items-center gap-2 text-[12px]">
        <span
          className="rounded-sm px-1 py-0.5 text-[10px] font-bold"
          style={{ background: "#fff", border: "1px solid #202124", color: "#202124" }}
        >
          Sponsored
        </span>
        <span className="text-[#202124]/70">{ad.finalUrl.replace("https://", "")}</span>
      </div>
      <a className="text-[20px] leading-tight text-[#1a0dab] hover:underline" href="#">
        {h1} | {h2}
      </a>
      <div className="mt-1 text-[12px] text-[#006621]">
        {ad.finalUrl.replace("https://", "")} › {ad.pathA} › {ad.pathB}
      </div>
      <p className="mt-2 text-[13.5px] leading-snug text-[#4d5156]">
        {d1} {d2}
      </p>
      <div className="mt-3 grid grid-cols-2 gap-y-1 text-[13px] text-[#1a0dab]">
        {ad.sitelinks.slice(0, 4).map((sl) => (
          <a key={sl.title} className="hover:underline" href="#">
            {sl.title}
          </a>
        ))}
      </div>
      <div className="mt-3 text-[12px] text-[#4d5156]">{ad.callouts.join(" · ")}</div>
      <div className="mt-3 border-t border-black/10 pt-2 text-[11px] text-[#5f6368]">
        Headline 3: <span className="text-[#202124]">{h3}</span>
      </div>
    </div>
  );
};

const AssistantPanel = ({
  open,
  onClose,
  campaigns,
}: {
  open: boolean;
  onClose: () => void;
  campaigns: Campaign[];
}) => {
  const [mode, setMode] = useState<Mode>("standard");
  const [showWarning, setShowWarning] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([
    {
      id: "welcome",
      role: "assistant",
      content:
        "Hi! I'm your Google Ads assistant. Ask me anything about your campaigns — performance, trends, explanations.",
    },
  ]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [preview, setPreview] = useState<AdPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const requestMode = (m: Mode) => {
    if (m === "advanced") setShowWarning(true);
    else {
      setMode("standard");
      setPreview(null);
    }
  };

  const send = (text: string) => {
    const t = text.trim();
    if (!t) return;
    const userMsg: ChatMsg = { id: crypto.randomUUID(), role: "user", content: t };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setThinking(true);

    setTimeout(() => {
      let reply = "";
      if (mode === "standard") {
        reply = generateStandardReply(t, campaigns);
      } else if (pendingAction) {
        reply = `Got it — building this for *${t}*. I've drafted 8 headlines, 3 descriptions, sitelinks and callouts. Review the preview on the right and approve when you're happy.`;
        setPreviewLoading(true);
        setTimeout(() => {
          setPreview(SAMPLE_AD);
          setPreviewLoading(false);
        }, 900);
        setPendingAction(null);
      } else {
        reply =
          "Pick a Quick Action below to start building, or describe what you'd like to create — a new ad, ad group, sitelink, or keyword expansion.";
      }
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: reply }]);
      setThinking(false);
    }, 600);
  };

  const triggerQuickAction = (action: typeof QUICK_ACTIONS[number]) => {
    setMessages((m) => [
      ...m,
      { id: crypto.randomUUID(), role: "user", content: action.prompt },
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `Great! Which campaign or ad group would you like to add this **${action.title.toLowerCase()}** to?`,
      },
    ]);
    setPendingAction(action.key);
    setPreview(null);
  };

  const approve = () => {
    setPreview(null);
    toast.success("Asset approved and queued for your campaign");
    setMessages((m) => [
      ...m,
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "✅ Pushed to your campaign queue. It'll go live after the next sync.",
      },
    ]);
  };

  const regenerate = () => {
    setPreviewLoading(true);
    setTimeout(() => {
      setPreview({
        ...SAMPLE_AD,
        headlines: [...SAMPLE_AD.headlines].reverse(),
      });
      setPreviewLoading(false);
    }, 700);
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      <aside
        className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-[1100px] flex-col animate-slide-in-right"
        style={{ ...glass, borderRadius: 0, borderLeft: "1px solid rgba(255,255,255,0.08)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/5 px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{
                background: `${BLUE}1f`,
                border: `1px solid ${BLUE}55`,
                boxShadow: `0 0 24px ${BLUE}44`,
              }}
            >
              <Sparkles size={16} style={{ color: BLUE }} />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-white">Google Ads AI Assistant</h2>
              <p className="text-[11px] text-white/50">
                Powered by GPT-5 · Connected to your account
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div
              className="relative flex items-center rounded-full border border-white/10 bg-white/[0.03] p-0.5"
            >
              {(["standard", "advanced"] as Mode[]).map((m) => {
                const active = mode === m;
                const isAdv = m === "advanced";
                return (
                  <button
                    key={m}
                    onClick={() => requestMode(m)}
                    className="relative flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-bold capitalize transition-all"
                    style={{
                      background: active
                        ? isAdv
                          ? ORANGE
                          : BLUE
                        : "transparent",
                      color: active ? (isAdv ? "#000" : "#fff") : "rgba(255,255,255,0.6)",
                      boxShadow: active
                        ? `0 4px 18px ${isAdv ? ORANGE : BLUE}66`
                        : "none",
                    }}
                  >
                    {isAdv && <AlertTriangle size={11} />}
                    {m}
                  </button>
                );
              })}
            </div>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-white/60 transition-colors hover:bg-white/[0.08] hover:text-white"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Chat column */}
          <div
            className={`flex flex-col ${
              mode === "advanced" ? "w-[60%] border-r border-white/5" : "w-full"
            }`}
          >
            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} animate-fade-in`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed ${
                      m.role === "user" ? "text-white" : "text-white/85"
                    }`}
                    style={
                      m.role === "user"
                        ? { background: BLUE, boxShadow: `0 8px 24px ${BLUE}55` }
                        : {
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.06)",
                          }
                    }
                  >
                    {m.content.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((chunk, i) => {
                      if (chunk.startsWith("**") && chunk.endsWith("**"))
                        return (
                          <strong key={i} className="font-bold text-white">
                            {chunk.slice(2, -2)}
                          </strong>
                        );
                      if (chunk.startsWith("*") && chunk.endsWith("*"))
                        return (
                          <em key={i} className="italic text-white/90">
                            {chunk.slice(1, -1)}
                          </em>
                        );
                      return <span key={i}>{chunk}</span>;
                    })}
                  </div>
                </div>
              ))}
              {thinking && (
                <div className="flex justify-start">
                  <div
                    className="flex items-center gap-2 rounded-2xl px-4 py-2.5 text-[13px] text-white/60"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                  >
                    <Loader2 size={13} className="animate-spin" />
                    Thinking…
                  </div>
                </div>
              )}
            </div>

            {/* Quick actions (advanced) */}
            {mode === "advanced" && (
              <div className="border-t border-white/5 px-6 py-4">
                <div className="mb-3 text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/40">
                  Quick Actions
                </div>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {QUICK_ACTIONS.map((a) => {
                    const Icon = a.icon;
                    return (
                      <button
                        key={a.key}
                        onClick={() => triggerQuickAction(a)}
                        className="group min-w-[170px] flex-shrink-0 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-left transition-all hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.06]"
                      >
                        <div className="mb-2 flex items-center gap-2">
                          <div
                            className="flex h-7 w-7 items-center justify-center rounded-lg"
                            style={{ background: `${BLUE}1a`, color: BLUE }}
                          >
                            {a.key === "optimize" ? <Wand2 size={13} /> : <Plus size={13} />}
                          </div>
                          <Icon size={13} className="text-white/50" />
                        </div>
                        <div className="text-[12.5px] font-bold text-white">{a.title}</div>
                        <div className="mt-0.5 text-[10.5px] leading-snug text-white/50">{a.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="border-t border-white/5 px-6 py-4">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  send(input);
                }}
                className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 focus-within:border-white/25"
              >
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={
                    mode === "standard"
                      ? "Ask about performance, trends, top ads..."
                      : pendingAction
                      ? "Describe the campaign or ad group..."
                      : "Describe what to build, or pick a quick action above"
                  }
                  className="flex-1 bg-transparent text-[13.5px] text-white placeholder:text-white/35 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={!input.trim()}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-white transition-all disabled:opacity-30"
                  style={{
                    background: BLUE,
                    boxShadow: input.trim() ? `0 6px 18px ${BLUE}66` : "none",
                  }}
                >
                  <Send size={13} />
                </button>
              </form>
            </div>
          </div>

          {/* Preview pane */}
          {mode === "advanced" && (
            <div className="flex w-[40%] flex-col bg-black/20">
              <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/50">
                  Live Preview
                </div>
                <div className="flex rounded-lg border border-white/10 bg-white/[0.03] p-0.5">
                  {(["desktop", "mobile"] as const).map((d) => (
                    <button
                      key={d}
                      onClick={() => setDevice(d)}
                      className="flex items-center gap-1 rounded-md px-2.5 py-1 text-[10.5px] font-semibold capitalize transition-colors"
                      style={{
                        background: device === d ? "rgba(255,255,255,0.08)" : "transparent",
                        color: device === d ? "#fff" : "rgba(255,255,255,0.5)",
                      }}
                    >
                      {d === "desktop" ? <Monitor size={11} /> : <Smartphone size={11} />}
                      {d}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-6">
                {previewLoading ? (
                  <div className="flex flex-col items-center gap-3 text-white/60">
                    <Loader2 size={26} className="animate-spin" style={{ color: BLUE }} />
                    <div className="text-[12px]">AI is composing your ad…</div>
                  </div>
                ) : preview ? (
                  <div className="w-full animate-scale-in">
                    <AdPreviewCard ad={preview} device={device} />
                    <div className="mt-5 flex items-center justify-center gap-2">
                      <button
                        onClick={regenerate}
                        className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-2 text-[12px] font-semibold text-white/80 transition-colors hover:bg-white/[0.06]"
                      >
                        <RefreshCw size={12} /> Regenerate
                      </button>
                      <button
                        onClick={approve}
                        className="flex items-center gap-1.5 rounded-lg px-5 py-2 text-[12.5px] font-bold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
                        style={{ background: BLUE, boxShadow: `0 8px 24px ${BLUE}77` }}
                      >
                        <CheckCircle2 size={13} /> Approve & Add to Campaign
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-white/40">
                    <div
                      className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl"
                      style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                    >
                      <Sparkles size={18} className="text-white/40" />
                    </div>
                    <p className="text-[12.5px]">Pick a Quick Action or ask the assistant to build something.</p>
                    <p className="mt-1 text-[11px] text-white/30">Your live preview will appear here.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>

      {showWarning && (
        <AdvancedWarningModal
          onCancel={() => setShowWarning(false)}
          onConfirm={() => {
            setShowWarning(false);
            setMode("advanced");
          }}
        />
      )}
    </>
  );
};

/* ======================================================================
   MAIN TAB
====================================================================== */
type TabKey = "overview" | "aria" | "performance" | "search" | "insights";

const TABS: { key: TabKey; label: string; icon: any }[] = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "aria", label: "ARIA", icon: Sparkles },
  { key: "performance", label: "Performance", icon: BarChart3 },
  { key: "search", label: "Search Terms", icon: SearchIcon },
  { key: "insights", label: "Insights", icon: Lightbulb },
];

const GoogleAdsTab = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>(INITIAL_CAMPAIGNS);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [ariaTrigger, setAriaTrigger] = useState<AriaTrigger>(null);
  // Recommendations available → pulse the FAB stronger
  const recsAvailable = true;

  const totals = useMemo(() => {
    const clicks = campaigns.reduce((s, c) => s + c.clicks, 0);
    const impressions = campaigns.reduce((s, c) => s + c.impressions, 0);
    const spend = campaigns.reduce((s, c) => s + c.spend, 0);
    const conversions = campaigns.reduce((s, c) => s + c.conversions, 0);
    return {
      clicks,
      impressions,
      spend,
      conversions,
      cpc: spend / Math.max(1, clicks),
    };
  }, [campaigns]);

  const toggle = (id: string) =>
    setCampaigns((prev) =>
      prev.map((c) => (c.id === id ? { ...c, enabled: !c.enabled } : c)),
    );

  const triggerAria = (prompt: string, ctx?: Record<string, any>) => {
    setActiveTab("aria");
    setAriaTrigger({ prompt, context: ctx, forceAdvanced: true });
  };

  // Demo tour: external control of the Google Ads inner tab
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (typeof detail === "string" && TABS.some((t) => t.key === detail)) {
        setActiveTab(detail as TabKey);
        if (detail !== "aria") setAriaTrigger(null);
      }
    };
    window.addEventListener("orbit:ads-set-tab", handler);
    return () => window.removeEventListener("orbit:ads-set-tab", handler);
  }, []);

  return (
    <div
      className="animate-fade-in relative -mx-4 min-h-[calc(100vh-180px)] rounded-[24px] px-4 py-6 md:-mx-8 md:px-8"
      style={{
        background: BG,
        fontFamily: "'Inter', system-ui, sans-serif",
        backgroundImage:
          "radial-gradient(1200px 600px at 10% -10%, rgba(66,133,244,0.10), transparent 60%), radial-gradient(900px 500px at 110% 0%, rgba(234,88,12,0.08), transparent 60%)",
      }}
    >
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl"
            style={glass}
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
          </div>
          <div>
            <h1 className="text-[18px] font-bold tracking-tight text-white">Google Ads</h1>
            <p className="text-[12px] text-white/50">Last 30 days · Live data</p>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
          style={{
            background: "rgba(52,168,83,0.12)",
            border: "1px solid rgba(52,168,83,0.35)",
            color: GREEN,
          }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: GREEN, boxShadow: `0 0 10px ${GREEN}` }}
          />
          Connected
        </span>
      </div>

      {/* Top Nav (sticky) */}
      <div className="sticky top-2 z-30 mb-6">
        <div
          className="flex w-full overflow-x-auto rounded-2xl p-1.5"
          style={glass}
        >
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            const isAria = t.key === "aria";
            return (
              <button
                key={t.key}
                data-tour-id={`ads-tab-${t.key}`}
                onClick={() => {
                  setActiveTab(t.key);
                  if (t.key !== "aria") setAriaTrigger(null);
                }}
                className={`group relative flex flex-shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-[12.5px] font-bold transition-all ${
                  isAria ? "px-5" : ""
                }`}
                style={{
                  background: active
                    ? isAria
                      ? `linear-gradient(135deg, ${BURNT}, #b8390a)`
                      : "rgba(255,255,255,0.07)"
                    : "transparent",
                  color: active ? "#fff" : "rgba(255,255,255,0.6)",
                  boxShadow: active && isAria ? `0 8px 24px ${BURNT}66, inset 0 0 0 1px ${BURNT}88` : "none",
                }}
              >
                {isAria ? (
                  <span className="relative flex h-4 w-4 items-center justify-center">
                    <Icon size={13} style={{ color: active ? "#fff" : BURNT_GLOW }} />
                    <span
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: BURNT,
                        filter: "blur(6px)",
                        opacity: active ? 0.7 : 0.45,
                        animation: "pulse 2.4s ease-in-out infinite",
                        zIndex: -1,
                      }}
                    />
                  </span>
                ) : (
                  <Icon size={13} />
                )}
                <span className="uppercase tracking-[0.08em]">{t.label}</span>
                {isAria && (
                  <span
                    className="hidden text-[9.5px] font-bold uppercase tracking-[0.14em] opacity-70 md:inline"
                    style={{ color: active ? "rgba(255,255,255,0.85)" : BURNT_GLOW }}
                  >
                    · AI
                  </span>
                )}
                {!active && !isAria && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity group-hover:opacity-100"
                    style={{ background: "rgba(255,255,255,0.04)" }}
                  />
                )}
                {!active && isAria && (
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity group-hover:opacity-100"
                    style={{
                      background: `${BURNT}1a`,
                      boxShadow: `inset 0 0 0 1px ${BURNT}44, 0 0 24px ${BURNT}33`,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === "overview" && (
        <div className="animate-fade-in">
          {/* Metric cards */}
          <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-5">
            <MetricCard label="Clicks" value={totals.clicks.toLocaleString()} delta="18.2%" up accent={BLUE} seed={1} />
            <MetricCard label="Impressions" value={`${(totals.impressions / 1000).toFixed(0)}K`} delta="11.4%" up accent={BLUE} seed={2} />
            <MetricCard label="Avg CPC" value={`$${totals.cpc.toFixed(2)}`} delta="6.8%" up={false} accent={ORANGE} seed={3} />
            <MetricCard label="Total Cost" value={`$${totals.spend.toLocaleString()}`} delta="3.1%" up accent={ORANGE} seed={4} />
            <MetricCard label="Conversions" value={String(totals.conversions)} delta="27.9%" up accent={GREEN} seed={5} />
          </div>

          {/* Campaigns table */}
          <CampaignsTable campaigns={campaigns} onToggle={toggle} />
        </div>
      )}

      {activeTab === "aria" && (
        <div className="animate-fade-in">
          <AriaTab trigger={ariaTrigger} />
        </div>
      )}

      {activeTab === "performance" && (
        <div className="animate-fade-in">
          <PerformanceTab />
        </div>
      )}

      {activeTab === "search" && (
        <div className="animate-fade-in">
          <SearchTermsTab mode="standard" onTriggerAria={triggerAria} />
        </div>
      )}

      {activeTab === "insights" && (
        <div className="animate-fade-in">
          <InsightsTab onTriggerAria={(p, ctx) => triggerAria(p, ctx)} />
        </div>
      )}

      {/* Floating Assistant FAB */}
      <button
        onClick={() => setAssistantOpen(true)}
        aria-label="Open Google Ads AI Assistant"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full transition-transform hover:scale-105 active:scale-95"
        style={{
          background: `linear-gradient(135deg, ${BURNT}, #b8390a)`,
          boxShadow: `0 12px 40px ${BURNT}99, 0 0 0 1px rgba(255,255,255,0.1) inset`,
        }}
      >
        <span
          className="absolute inset-0 rounded-full opacity-70"
          style={{
            background: BURNT,
            filter: "blur(18px)",
            zIndex: -1,
            animation: recsAvailable ? "pulse 1.6s ease-in-out infinite" : "pulse 2.4s ease-in-out infinite",
          }}
        />
        <MessageCircle size={22} className="text-white" />
        <span
          className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
          style={{ background: BURNT_GLOW, boxShadow: `0 4px 12px ${BURNT}88` }}
        >
          AI
        </span>
      </button>

      <AssistantPanel
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        campaigns={campaigns}
      />
    </div>
  );
};

export default GoogleAdsTab;
